import React, { useCallback, useEffect, useRef, useState } from "react";
import { MessageCircle, MonitorSmartphone } from "lucide-react";
import { HOUSE_BRAND, type WhatsappTransport } from "@school-sales-ai/shared";
import { Button } from "@/components/ui/button";
import { loadFacebookSdk, type FacebookSdk } from "@/lib/facebook";
import { currentBrowserKind, type BrowserKind } from "@/lib/browser-env";
import { runWithPopupWatch } from "@/lib/popup-watch";
import { useConnectWaba } from "@/hooks/useConnectWaba";
import { useHouseBrandNotice } from "@/hooks/useHouseBrandNotice";
import { useRecordWabaSignupEvent } from "@/hooks/useRecordWabaSignupEvent";
import type { WabaSignupEventInput } from "@/hooks/useRecordWabaSignupEvent";

const APP_ID = import.meta.env.VITE_META_APP_ID as string | undefined;
const CONFIG_ID = import.meta.env.VITE_META_CONFIG_ID as string | undefined;
// Optional: Meta may require a separate Embedded Signup configuration for the coexistence flow.
// Falls back to the standard config when unset, so a single config can serve both.
const COEXISTENCE_CONFIG_ID = import.meta.env.VITE_META_CONFIG_ID_COEXISTENCE as string | undefined;
// FB JS SDK graph version. Align with the backend WABA_GRAPH_API_VERSION during the onboarding spike
// (docs/plans/whatsapp-official-api-onboarding.md, Paso 0).
const FB_GRAPH_VERSION = "v23.0";
// Meta posts the Embedded Signup result via postMessage from these origins.
const FB_ORIGINS = ["https://www.facebook.com", "https://web.facebook.com"];
// `extras.featureType` that asks Meta for the coexistence variant (link an existing WhatsApp
// Business app number by QR instead of registering a fresh one).
const COEXISTENCE_FEATURE_TYPE = "whatsapp_business_app_onboarding";
// How long we wait with NO signal at all from Meta before saying so. Long on purpose: the
// coexistence flow includes scanning a QR with a second device, and a premature "no pasó nada"
// on a flow that is merely slow is worse than a spinner. The certain case — a popup the browser
// refused — is caught immediately by `runWithPopupWatch` and never waits for this.
const NO_SIGNAL_HINT_MS = 45_000;

type SignupData = { wabaId: string; phoneNumberId: string };

/** The `data` Meta attaches to a WA_EMBEDDED_SIGNUP message — result or failure. */
type MetaSignupData = {
  waba_id?: string;
  phone_number_id?: string;
  error_message?: string;
  error_id?: string | number;
  session_id?: string;
  current_step?: string;
};

// The popup signals completion with `FINISH` on the standard flow and
// `FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING` on the coexistence one.
function isFinishEvent(event: string | undefined): boolean {
  return event === "FINISH" || event === "FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING";
}

/**
 * Turns Meta's ERROR payload into something the operator can act on.
 *
 * The two ids are kept in the message on purpose: they are exactly what Meta's developer
 * support asks for, and without them a failed onboarding is a shrug. This used to fall
 * through the handler entirely, so a coexistence attempt that Meta actively rejected
 * looked identical to one that silently never finished.
 */
function describeMetaError(data: MetaSignupData | undefined): string {
  const reason = data?.error_message?.trim();
  const ids = [
    data?.error_id !== undefined ? `error_id ${String(data.error_id)}` : null,
    data?.session_id ? `session_id ${data.session_id}` : null,
  ].filter((part): part is string => part !== null);

  const base = `Meta rechazó la conexión: ${reason && reason.length > 0 ? reason : "no detalló el motivo"}`;
  return ids.length > 0 ? `${base} (${ids.join(" · ")})` : base;
}

/**
 * Files a failed attempt with the backend so it survives the browser session.
 *
 * Fire-and-forget by design: the signup has already failed and the operator is reading Meta's
 * reason on screen; a failure to file must not surface as a second error. Meta's `error_id` can
 * arrive as a number, hence the `String()`.
 */
function reportFailure(
  report: (input: WabaSignupEventInput) => void,
  event: string,
  data: MetaSignupData | undefined,
): void {
  report({
    event,
    currentStep: data?.current_step ?? null,
    errorId: data?.error_id !== undefined ? String(data.error_id) : null,
    sessionId: data?.session_id ?? null,
    errorMessage: data?.error_message ?? null,
  });
}

/** The standing warning shown before the operator clicks, or null when the browser is fine. */
function environmentWarning(kind: BrowserKind): string | null {
  if (kind === "in_app_webview") {
    return "Estás dentro del navegador de otra app (WhatsApp, Instagram, Facebook…), que no permite abrir la ventana de Meta. Abre esta página en Chrome o Safari — de preferencia en una computadora.";
  }
  if (kind === "mobile") {
    return "Meta abre este registro en una ventana emergente que los navegadores de celular suelen bloquear. Si puedes, conéctalo desde una computadora.";
  }
  return null;
}

/**
 * "Conectar WhatsApp API" — launches Meta's Embedded Signup and connects the client's WABA.
 *
 * Two async signals must be joined before we can call the backend:
 *  - the `code` from the FB.login callback, and
 *  - the `waba_id` + `phone_number_id` from the WA_EMBEDDED_SIGNUP window message.
 * Either can arrive first, so both are stashed in refs and `tryComplete()` fires the mutation once both
 * are present (guarded so it submits exactly once per run).
 *
 * `transport` picks the flow: `cloud_api` registers a fresh number, `cloud_api_coexistence` links a
 * number already running in the WhatsApp Business app (QR step, advisors keep replying from their
 * phone). The backend preserves whichever transport the super-admin configured.
 *
 * **The SDK is preloaded on mount and `FB.login` is called synchronously from the click.** The SDK
 * opens its popup inside that call, and a popup only opens while the browser still considers the
 * click active — so awaiting a network fetch first (which is what this did) loses the user gesture
 * and the popup is refused with no error anywhere. Desktop browsers forgive that; phones do not,
 * which is why the flow "se quedaba cargando" on Android with nothing on screen.
 */
export function WabaEmbeddedSignup({
  transport,
}: {
  transport: WhatsappTransport;
}): React.JSX.Element {
  const connect = useConnectWaba();
  // `mutate` is stable across renders, so the message listener below can depend on it without
  // re-subscribing every render.
  const { mutate: reportSignupEvent } = useRecordWabaSignupEvent();
  const isCoexistence = transport === "cloud_api_coexistence";
  // Meta's pop-up names the app, which is the house brand, not the client's.
  const noticeBrand = useHouseBrandNotice();
  const [launching, setLaunching] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  // Separate from `localError`: nothing failed, we just have nothing to report yet.
  const [hint, setHint] = useState<string | null>(null);
  const [browserKind] = useState<BrowserKind>(() => currentBrowserKind());

  const sdkRef = useRef<FacebookSdk | null>(null);
  const codeRef = useRef<string | null>(null);
  const signupRef = useRef<SignupData | null>(null);
  const submittedRef = useRef(false);
  // Set when Meta reported a real reason, so the generic "no llegó la información"
  // fallback below never overwrites a specific error with a guess.
  const metaErrorRef = useRef(false);
  const noSignalTimerRef = useRef<number | null>(null);

  // Preload the SDK so the click path is synchronous. Failures are swallowed here and surfaced on
  // click, where the operator is actually waiting for an answer.
  useEffect(() => {
    if (!APP_ID || !CONFIG_ID) return;
    let cancelled = false;
    void loadFacebookSdk(APP_ID, FB_GRAPH_VERSION)
      .then((fb) => {
        if (!cancelled) sdkRef.current = fb;
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  const clearNoSignalTimer = useCallback(() => {
    if (noSignalTimerRef.current !== null) {
      window.clearTimeout(noSignalTimerRef.current);
      noSignalTimerRef.current = null;
    }
  }, []);

  useEffect(() => clearNoSignalTimer, [clearNoSignalTimer]);

  const tryComplete = useCallback(() => {
    if (submittedRef.current) return;
    const code = codeRef.current;
    const signup = signupRef.current;
    if (!code || !signup) return;
    submittedRef.current = true;
    connect.mutate(
      { code, wabaId: signup.wabaId, phoneNumberId: signup.phoneNumberId },
      { onError: () => (submittedRef.current = false) },
    );
  }, [connect]);

  useEffect(() => {
    function onMessage(event: MessageEvent): void {
      if (!FB_ORIGINS.includes(event.origin)) return;
      let payload: unknown;
      try {
        payload = typeof event.data === "string" ? JSON.parse(event.data) : event.data;
      } catch {
        return; // not our JSON message
      }
      const msg = payload as {
        type?: string;
        event?: string;
        data?: MetaSignupData;
      };
      if (msg.type !== "WA_EMBEDDED_SIGNUP") return;

      // Any event at all means the popup is alive and talking to us — whatever it says next,
      // the "nothing ever happened" hint would now be a lie.
      clearNoSignalTimer();
      setHint(null);

      // Log every event, including the ones with no branch below. Meta adds event names
      // over time, and dropping the unknown ones silently is what left a failed
      // coexistence onboarding with nothing to go on.
      console.info("[waba-signup]", msg.event ?? "(sin evento)", msg.data);

      if (isFinishEvent(msg.event) && msg.data?.waba_id && msg.data.phone_number_id) {
        signupRef.current = { wabaId: msg.data.waba_id, phoneNumberId: msg.data.phone_number_id };
        tryComplete();
      } else if (msg.event === "ERROR") {
        metaErrorRef.current = true;
        setLaunching(false);
        setLocalError(describeMetaError(msg.data));
        reportFailure(reportSignupEvent, "ERROR", msg.data);
      } else if (msg.event === "CANCEL") {
        setLaunching(false);
        setLocalError(
          msg.data?.current_step
            ? `Conexión cancelada en el paso "${msg.data.current_step}".`
            : "Conexión cancelada.",
        );
        // Filed too, not just errors: a tenant who backs out at the same step every time is
        // telling us something a stack of ERRORs would not.
        reportFailure(reportSignupEvent, "CANCEL", msg.data);
      }
    }
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [tryComplete, reportSignupEvent, clearNoSignalTimer]);

  /** What we tell the operator when the browser refused to open Meta's window. */
  const popupBlockedMessage = useCallback((): string => {
    const remedy =
      browserKind === "in_app_webview"
        ? "Abre el CRM en Chrome o Safari (no dentro de otra app)."
        : browserKind === "mobile"
          ? "Conéctalo desde una computadora, o permite las ventanas emergentes para este sitio."
          : "Permite las ventanas emergentes para este sitio y vuelve a intentar.";
    return `Tu navegador bloqueó la ventana de Meta. ${remedy}`;
  }, [browserKind]);

  /** Calls FB.login. Must run inside the click's own task — see the component docblock. */
  const launch = useCallback(
    (fb: FacebookSdk): void => {
      const outcome = runWithPopupWatch(() =>
        fb.login(
          (response) => {
            // The callback means the popup closed (or never opened). Either way, waiting for a
            // first signal is over.
            clearNoSignalTimer();
            const code = response.authResponse?.code;
            if (!code) {
              setLaunching(false);
              setLocalError("No se recibió el código de autorización de Meta.");
              return;
            }
            codeRef.current = code;
            tryComplete();
            // If we have a login code but the WA_EMBEDDED_SIGNUP account data
            // (waba_id/phone_number_id) never arrived, the signup didn't finish — most commonly
            // because Meta blocks client registration until the app has advanced access approved.
            // Don't spin forever: after a short grace period, surface a clear message. Skipped
            // when Meta already said why (ERROR): a guess must never replace a reason.
            window.setTimeout(() => {
              if (
                codeRef.current &&
                !signupRef.current &&
                !submittedRef.current &&
                !metaErrorRef.current
              ) {
                setLaunching(false);
                setLocalError(
                  isCoexistence
                    ? "No se recibió la información de la cuenta de WhatsApp. Verifica que el número esté activo en la app de WhatsApp Business (versión 2.24.17 o superior) y que nunca se haya registrado en la API oficial."
                    : "No se recibió la información de la cuenta de WhatsApp. Si la app aún no tiene acceso avanzado aprobado por Meta, el registro de clientes está bloqueado hasta la aprobación.",
                );
                // Lowercase: ours, not Meta's. This is the silent shape — the popup closed with a
                // login code and never sent account data, and Meta told us nothing at all.
                reportSignupEvent({ event: "local_no_account_data" });
              }
            }, 5000);
          },
          {
            config_id: isCoexistence ? (COEXISTENCE_CONFIG_ID ?? CONFIG_ID ?? "") : (CONFIG_ID ?? ""),
            response_type: "code",
            override_default_response_type: true,
            extras: {
              setup: {},
              featureType: isCoexistence ? COEXISTENCE_FEATURE_TYPE : "",
              sessionInfoVersion: "3",
            },
          },
        ),
      );

      if (outcome === "blocked") {
        clearNoSignalTimer();
        setLaunching(false);
        setLocalError(popupBlockedMessage());
        // Filed like every other dead end, so "no me abre nada" arrives with something to read.
        reportSignupEvent({ event: "local_popup_blocked" });
        return;
      }

      // `opened` or `unknown`: we are now waiting on Meta. Stop the spinner if nothing whatsoever
      // comes back, so the operator is never left staring at a spinner with no way out. The flow
      // itself keeps working — completion is driven by the refs above, not by this state.
      noSignalTimerRef.current = window.setTimeout(() => {
        noSignalTimerRef.current = null;
        setLaunching(false);
        setHint(
          browserKind === "desktop"
            ? "No hemos recibido respuesta de Meta. Si la ventana no llegó a abrirse, revisa el bloqueador de ventanas emergentes."
            : "No hemos recibido respuesta de Meta. Si nunca se abrió la ventana, tu navegador la bloqueó: conéctalo desde una computadora.",
        );
      }, NO_SIGNAL_HINT_MS);
    },
    [
      tryComplete,
      isCoexistence,
      reportSignupEvent,
      clearNoSignalTimer,
      popupBlockedMessage,
      browserKind,
    ],
  );

  const handleClick = useCallback((): void => {
    if (!APP_ID || !CONFIG_ID) {
      setLocalError("Falta configurar VITE_META_APP_ID / VITE_META_CONFIG_ID.");
      return;
    }
    setLocalError(null);
    setHint(null);
    setLaunching(true);
    codeRef.current = null;
    signupRef.current = null;
    submittedRef.current = false;
    metaErrorRef.current = false;
    clearNoSignalTimer();

    const ready = sdkRef.current;
    if (ready) {
      // Synchronous: the popup opens inside the click's own task, which is the only way a phone
      // allows it at all.
      launch(ready);
      return;
    }

    // The preload hasn't resolved yet (first paint, slow network). Awaiting it costs the user
    // gesture and the popup will very likely be refused — which `runWithPopupWatch` detects and
    // reports, instead of hanging.
    void loadFacebookSdk(APP_ID, FB_GRAPH_VERSION)
      .then((fb) => {
        sdkRef.current = fb;
        launch(fb);
      })
      .catch((err: unknown) => {
        setLaunching(false);
        setLocalError(err instanceof Error ? err.message : "No se pudo cargar el SDK de Meta.");
      });
  }, [launch, clearNoSignalTimer]);

  const errorMessage =
    localError ??
    (connect.isError
      ? connect.error instanceof Error
        ? connect.error.message
        : "Error al conectar."
      : null);
  const busy = launching || connect.isPending;
  const warning = environmentWarning(browserKind);

  return (
    <div className="flex w-full max-w-sm flex-col items-center gap-6 rounded-xl border border-ink-100 bg-white p-8 shadow-sm">
      <div className="flex h-14 w-14 items-center justify-center rounded-full bg-green-100">
        <MessageCircle className="text-green-600" size={26} />
      </div>
      <div className="text-center">
        <p className="text-base font-semibold text-ink-900">
          {isCoexistence ? "WhatsApp API (coexistencia)" : "WhatsApp API"}
        </p>
        <p className="mt-1 text-sm text-ink-500">
          {isCoexistence
            ? "Vincula el número que el colegio ya usa en la app de WhatsApp Business escaneando un código QR. Los asesores siguen contestando desde su celular."
            : "Conecta el número de WhatsApp Business del colegio con la API oficial de Meta."}
        </p>
        {noticeBrand && (
          <p className="mt-2 text-xs text-ink-500">
            Meta te mostrará «{HOUSE_BRAND.name}», la empresa detrás de {noticeBrand.name}.
          </p>
        )}
        {isCoexistence && (
          <p className="mt-2 text-xs text-ink-400">
            Requiere la app de WhatsApp Business 2.24.17 o superior y un número que nunca se haya
            registrado en la API oficial.
          </p>
        )}
      </div>

      {warning && (
        <div className="flex items-start gap-2 rounded-lg bg-amber-50 p-3 text-left text-xs text-amber-800">
          <MonitorSmartphone className="mt-0.5 shrink-0" size={14} />
          <span>{warning}</span>
        </div>
      )}

      <Button onClick={handleClick} disabled={busy} className="gap-2">
        {busy ? (
          <>
            <div className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/40 border-t-white" />
            Conectando…
          </>
        ) : (
          <>
            <MessageCircle size={16} />
            Conectar WhatsApp API
          </>
        )}
      </Button>

      {errorMessage && <p className="text-center text-xs text-red-600">{errorMessage}</p>}
      {!errorMessage && hint && <p className="text-center text-xs text-ink-500">{hint}</p>}
    </div>
  );
}
